import {
  defaultGroupContentWithOneItem,
  defaultImageContentWidget,
} from "@prismicio/editor-fields"
import { isEmpty } from "@prismicio/editor-support/Object"
import { type SliceItemContent } from "@prismicio/types-internal/lib/content"

import type { SectionSliceConfig } from "../sliceTableData"
import { createSliceKey } from "./createSliceKey"

interface CreateEmptySliceArgs {
  config: SectionSliceConfig
}

export function createEmptySlice(args: CreateEmptySliceArgs): SliceItemContent {
  const { config } = args

  if (config.sliceConfig.type === "Color") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "FieldContent",
        type: config.sliceConfig.type,
        value: "",
      },
    }
  }

  if (config.sliceConfig.type === "Group") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: defaultGroupContentWithOneItem,
    }
  }

  if (config.sliceConfig.type === "Boolean") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "BooleanContent",
        value: false,
      },
    }
  }

  if (config.sliceConfig.type === "Date") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "FieldContent",
        type: config.sliceConfig.type,
        value: "",
      },
    }
  }

  if (config.sliceConfig.type === "Text") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "FieldContent",
        type: config.sliceConfig.type,
        value: "",
      },
    }
  }

  if (config.sliceConfig.type === "Number") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "FieldContent",
        type: config.sliceConfig.type,
        value: "",
      },
    }
  }

  if (config.sliceConfig.type === "Range") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "FieldContent",
        type: config.sliceConfig.type,
        value: "",
      },
    }
  }

  if (config.sliceConfig.type === "Select") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "FieldContent",
        type: config.sliceConfig.type,
        value: "",
      },
    }
  }

  if (config.sliceConfig.type === "Timestamp") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "FieldContent",
        type: config.sliceConfig.type,
        value: "",
      },
    }
  }

  if (config.sliceConfig.type === "GeoPoint") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "GeoPointContent",
        position: {
          lat: 0,
          lng: 0,
        },
      },
    }
  }

  if (config.sliceConfig.type === "Embed") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "EmbedContent",
        all: undefined,
        embed_url: "",
        type: "",
      },
    }
  }

  if (config.sliceConfig.type === "Image") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: defaultImageContentWidget,
    }
  }

  if (config.sliceConfig.type === "IntegrationFields") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "IntegrationFieldsContent",
        value: "",
      },
    }
  }

  if (config.sliceConfig.type === "Link") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "LinkContent",
        key: crypto.randomUUID(),
        value: {
          __TYPE__: "ExternalLink",
          url: "",
        },
      },
    }
  }

  if (config.sliceConfig.type === "Separator") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "SeparatorContent",
      },
    }
  }

  if (config.sliceConfig.type === "StructuredText") {
    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "StructuredTextContent",
        value: [],
      },
    }
  }

  if (config.sliceConfig.type === "Slice") {
    const hasRepeatableContent =
      !config.sliceConfig.repeat || isEmpty(config.sliceConfig.repeat)

    return {
      key: createSliceKey({ sliceId: config.sliceReference }),
      maybeLabel: undefined,
      name: config.sliceReference,
      widget: {
        __TYPE__: "CompositeSliceContent",
        nonRepeat: {},
        repeat: hasRepeatableContent
          ? []
          : [{ __TYPE__: "GroupItemContent", value: [] }],
      },
    }
  }

  if (config.sliceConfig.type === "SharedSlice") {
    return {
      key: createSliceKey({ sliceId: config.sliceConfig.id }),
      maybeLabel: undefined,
      name: config.sliceConfig.id,
      widget: {
        __TYPE__: "SharedSliceContent",
        variation: config.sliceVariation,
        primary: {},
        items: [],
      },
    }
  }

  return {
    key: "",
    maybeLabel: undefined,
    name: "",
    widget: {
      __TYPE__: "SharedSliceContent",
      variation: "",
      primary: {},
      items: [],
    },
  }
}
